import "./trainers.css";
import { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import api from "../api";
import { useNavigate } from "react-router-dom";
import CustomSelect from "../CustomSelect";

const TrainerProfile = () => {
  const [trainerEdit, setTrainerEdit] = useState(null);
  const [originalTrainer, setOriginalTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const navigate = useNavigate();

  const statoOccupazionaleOptions = [
    { value: "Occupato", label: "Occupato" },
    { value: "Disoccupato", label: "Disoccupato" },
    { value: "Studente", label: "Studente" },
    { value: "In cerca di lavoro", label: "In cerca di lavoro" },
  ];

  const livelloOptions = [
    { value: "Basso", label: "Basso" },
    { value: "Medio", label: "Medio" },
    { value: "Alto", label: "Alto" },
  ];

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (!userRole || userRole !== "TRAINER") {
      alert("Accedi per visualizzare il profilo!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchTrainer();
    }
  }, [navigate]);

  const fetchTrainer = async () => {
    try {
      setLoading(true);
      const res = await api.get("/trainers/anagrafica");
      if (res.status === 200) {
        setTrainerEdit({ anagrafica: res.data });
        setOriginalTrainer({ anagrafica: res.data });
      } else {
        toast.error("Trainer non esistente!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (err) {
      console.error("Errore durante il caricamento dell'allenatore", err);
      toast.error("Qualcosa è andato storto!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatLabel = (field, value) => {
    if (!value || value.trim() === "") return "-";
    return value;
  };

  const validators = {
    cellulare: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Cellulare non può essere vuoto!";
      return /^\+?\d{9,15}$/.test(val)
        ? ""
        : "Il campo Cellulare ha un formato non valido (es. +391234567890)";
    },
    cittàDiResidenza: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Città di residenza non può essere vuoto!";
      return val.length >= 2 ? "" : "Inserisci una città valida";
    },
    via: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Via non può essere vuoto!";
      return val.length >= 2 ? "" : "Inserisci una via valida";
    },
    cap: (val) => {
      if (!val || val.trim() === "")
        return "Il campo CAP non può essere vuoto!";
      return /^\d{5}$/.test(val)
        ? ""
        : "Il campo CAP deve essere un codice postale valido (5 cifre)";
    },
    provincia: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Provincia non può essere vuoto!";
      return /^[A-Z]{2}$/.test(val)
        ? ""
        : "Il campo Provincia deve contenere 2 lettere maiuscole";
    },
    statoOccupazionale: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Stato occupazionale non può essere vuoto!";
      return statoOccupazionaleOptions.some((opt) => opt.value === val)
        ? ""
        : "Valore non valido per il campo Stato occupazionale";
    },
    livelloInglese: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Livello inglese non può essere vuoto!";
      return livelloOptions.some((opt) => opt.value === val)
        ? ""
        : "Valore non valido per il campo Livello inglese";
    },
    livelloInformatica: (val) => {
      if (!val || val.trim() === "")
        return "Il campo Livello informatica non può essere vuoto!";
      return livelloOptions.some((opt) => opt.value === val)
        ? ""
        : "Valore non valido per il campo Livello informatica";
    },
  };

  const validate = () => {
    const errors = {};
    if (!trainerEdit || !trainerEdit.anagrafica) return errors;
    for (const field of Object.keys(validators)) {
      const value = trainerEdit.anagrafica[field];
      const error = validators[field](value);
      if (error) errors[field] = error;
    }
    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTrainerEdit((prev) => ({
      ...prev,
      anagrafica: {
        ...prev.anagrafica,
        [name]: value,
      },
    }));

    if (validators[name]) {
      const error = validators[name](value);
      setInputErrors((prevErrors) => {
        const updated = { ...prevErrors };
        if (error) {
          updated[name] = error;
        } else {
          delete updated[name];
        }
        return updated;
      });
    }
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      const payload = {
        id: trainerEdit.anagrafica.id,
        cellulare: trainerEdit.anagrafica.cellulare,
        cittàDiResidenza: trainerEdit.anagrafica.cittàDiResidenza,
        via: trainerEdit.anagrafica.via,
        cap: trainerEdit.anagrafica.cap,
        provincia: trainerEdit.anagrafica.provincia,
        statoOccupazionale: trainerEdit.anagrafica.statoOccupazionale,
        livelloInglese: trainerEdit.anagrafica.livelloInglese,
        livelloInformatica: trainerEdit.anagrafica.livelloInformatica,
      };

      const res = await api.put(
        `/trainers/anagrafica/modifica?id=${trainerEdit.anagrafica.id}`,
        payload
      );

      if (res.status === 200) {
        toast.success("Trainer aggiornato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setIsEditing(false);
        setOriginalTrainer({ ...trainerEdit });
      } else if (res.status === 404) {
        toast.error("Trainer non trovato!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else if (res.status === 403) {
        toast.error("Non autorizzato!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (err) {
      console.error("Errore durante l'aggiornamento del trainer:", err);
      toast.error("Impossibile aggiornare il trainer!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleCancel = () => {
    setTrainerEdit({ ...originalTrainer });
    setIsEditing(false);
    setInputErrors({});
  };

  if (loading || !trainerEdit) return <div>Loading...</div>;

  const editableFields = [
    { name: "cellulare", label: "Cellulare", icon: "fa-phone" },
    { name: "cittàDiResidenza", label: "Città Di Residenza", icon: "fa-city" },
    { name: "via", label: "Via", icon: "fa-map-marker-alt" },
    { name: "cap", label: "Cap", icon: "fa-mail-bulk" },
    { name: "provincia", label: "Provincia", icon: "fa-map" },
    {
      name: "statoOccupazionale",
      label: "Stato Occupazionale",
      icon: "fa-briefcase",
    },
    { name: "livelloInglese", label: "Livello Inglese", icon: "fa-language" },
    {
      name: "livelloInformatica",
      label: "Livello Informatica",
      icon: "fa-laptop-code",
    },
  ];

  return (
    <div className="trainer-profile-modern-bg">
      {loading && (
        <div className="trainer-profile-modern-overlay">
          <div className="trainer-profile-modern-spinner"></div>
        </div>
      )}
      <div className="trainer-profile-modern-card">
        <div className="trainer-profile-modern-header">
          <h2>
            <i className="fa-solid fa-user-circle"></i>
            Profilo Trainer
          </h2>
          {!isEditing && (
            <button
              className="trainer-profile-modern-edit"
              onClick={() => setIsEditing(true)}
            >
              <i className="fa-solid fa-pen"></i>
              Modifica
            </button>
          )}
        </div>
        <div className="trainer-profile-modern-profile">
          <div className="trainer-profile-modern-avatar">
            <i className="fa-solid fa-user"></i>
          </div>
          <div className="trainer-profile-modern-info">
            <h3 className="trainer-profile-modern-name">
              {trainerEdit.anagrafica.nome} {trainerEdit.anagrafica.cognome}
            </h3>
            <p className="trainer-profile-modern-email">
              <i className="fa-solid fa-envelope"></i>
              {trainerEdit.anagrafica.email}
            </p>
            <p className="trainer-profile-modern-cf">
              <i className="fa-solid fa-id-card"></i>
              {trainerEdit.anagrafica.codiceFiscale}
            </p>
          </div>
        </div>
        <form
          className="trainer-profile-modern-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="trainer-profile-modern-fields">
            {editableFields.map(({ name, label, icon }) => (
              <div className="trainer-profile-modern-field" key={name}>
                <label className="trainer-profile-modern-label">
                  <i className={`fa-solid ${icon}`}></i>
                  {label}
                </label>
                {!isEditing ? (
                  <span className="trainer-profile-modern-value">
                    {formatLabel(name, trainerEdit.anagrafica[name])}
                  </span>
                ) : [
                    "statoOccupazionale",
                    "livelloInglese",
                    "livelloInformatica",
                  ].includes(name) ? (
                  <CustomSelect
                    options={
                      name === "statoOccupazionale"
                        ? statoOccupazionaleOptions
                        : livelloOptions
                    }
                    value={
                      (name === "statoOccupazionale"
                        ? statoOccupazionaleOptions
                        : livelloOptions
                      ).find(
                        (opt) => opt.value === trainerEdit.anagrafica[name]
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      handleChange({
                        target: {
                          name,
                          value: selectedOption ? selectedOption.value : "",
                        },
                      });
                    }}
                    placeholder={`Seleziona ${label}`}
                    className={inputErrors[name] ? "input-error" : ""}
                  />
                ) : (
                  <input
                    name={name}
                    value={trainerEdit.anagrafica[name] || ""}
                    onChange={handleChange}
                    className={inputErrors[name] ? "error" : ""}
                  />
                )}
                {inputErrors[name] && (
                  <span className="trainer-profile-modern-error">
                    <i className="fa-solid fa-exclamation-triangle"></i>
                    {inputErrors[name]}
                  </span>
                )}
              </div>
            ))}
          </div>
          {isEditing && (
            <div className="trainer-profile-modern-actions">
              <button
                type="button"
                className="trainer-profile-modern-cancel"
                onClick={handleCancel}
              >
                <i className="fa-solid fa-times"></i>
                Annulla
              </button>
              <button type="submit" className="trainer-profile-modern-save">
                <i className="fa-solid fa-save"></i>
                Salva modifiche
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TrainerProfile;
